import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * One canonical stat tile (resolves the 4+ divergent treatments). Value renders in the brand
 * display face (font-heading) with tabular-nums, and truncates so crore-scale ₹ never overflows.
 *
 * <StatCard label="Total Orders" value={31} icon={Package} accent="brand"
 *   trend="+12% vs last week" onClick={...} />
 *
 * props:
 *  - label, value
 *  - icon? (lucide component)
 *  - accent? 'brand'|'success'|'warning'|'danger'|'info'|'neutral'
 *  - variant? 'accent-bar' (default) | 'plain'
 *  - trend? (ReactNode shown under value)
 *  - loading? onClick?
 */
const ACCENTS = {
  brand:   { bar: "bg-brand",     chip: "bg-brand/10 text-brand" },
  success: { bar: "bg-green-500", chip: "bg-green-100 text-green-700" },
  warning: { bar: "bg-amber-500", chip: "bg-amber-100 text-amber-700" },
  danger:  { bar: "bg-red-500",   chip: "bg-red-100 text-red-700" },
  info:    { bar: "bg-blue-500",  chip: "bg-blue-100 text-blue-700" },
  neutral: { bar: "bg-slate-400", chip: "bg-slate-100 text-slate-600" },
};

export function StatCard({
  label,
  value,
  icon: Icon,
  accent = "brand",
  variant = "accent-bar",
  trend,
  loading = false,
  onClick,
  className,
}) {
  const a = ACCENTS[accent] || ACCENTS.brand;
  const clickable = typeof onClick === "function";

  return (
    <Card
      onClick={onClick}
      className={cn(
        "relative overflow-hidden border-0 shadow-sm",
        variant === "accent-bar" && "pl-1",
        clickable && "cursor-pointer transition-shadow hover:shadow-md",
        className
      )}
    >
      {variant === "accent-bar" && <span className={cn("absolute left-0 top-0 h-full w-1", a.bar)} />}
      <div className="flex items-start justify-between gap-3 p-5">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500 truncate">{label}</p>
          {loading ? (
            <div className="mt-2 h-8 w-20 animate-pulse rounded bg-slate-200" />
          ) : (
            <p className="mt-1 font-heading text-3xl font-bold tabular-nums text-slate-800 truncate">
              {value}
            </p>
          )}
          {trend && !loading && <p className="mt-1 text-xs text-slate-500 truncate">{trend}</p>}
        </div>
        {Icon && (
          <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", a.chip)}>
            <Icon className="h-5 w-5" />
          </span>
        )}
      </div>
    </Card>
  );
}

export default StatCard;
